import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Image as ImageIcon, Bold, Italic, Underline, Tag, CheckCircle, AlertCircle } from 'lucide-react';
import JoditEditor from 'jodit-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const EditCardPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('0.00');
  const [isFreeForMembers, setIsFreeForMembers] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  
  const fileInputRef = useRef(null);
  const editor = useRef(null);

  // Jodit Editor configuration
  const config = {
    readonly: false,
    placeholder: 'Write your card description here...',
    buttons: ['bold', 'italic', 'underline', 'link', 'ul', 'ol', 'font', 'fontsize', 'brush', 'paragraph'],
    height: 300,
    theme: 'default'
  };

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem('access_token');
  };

  // Fetch single card
  const fetchCard = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      const response = await fetch(`https://backend.dlapakistan.com/api/dla-shop/cards/${id}/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch card');
      }

      const card = await response.json();
      setTitle(card.title);
      setDescription(card.description);
      setPrice(card.price || '0.00');
      setIsFreeForMembers(card.is_free_for_members !== undefined ? card.is_free_for_members : true);
      
      if (card.image_url) {
        setExistingImage(card.image_url);
        setImagePreview(card.image_url);
      }

      // Store original data for comparison
      setOriginalData({
        title: card.title,
        description: card.description,
        price: card.price || '0.00',
        is_free_for_members: card.is_free_for_members !== undefined ? card.is_free_for_members : true,
        image_url: card.image_url
      });
    } catch (error) {
      console.error('Error fetching card:', error);
      setError('Failed to load card');
    } finally {
      setLoading(false);
    }
  };

  // Show Error Modal
  const showErrorModal = (errorMessage) => {
    MySwal.fire({
      title: (
        <div className="flex items-center justify-center space-x-2">
          <AlertCircle className="w-6 h-6 text-red-600" />
          <span className="text-xl font-bold text-red-700">Error!</span>
        </div>
      ),
      html: <p className="text-gray-700 text-center py-2">{errorMessage}</p>,
      showConfirmButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Try Again',
      showClass: {
        popup: 'animate__animated animate__shakeX'
      }
    });
  };

  // Show Edit Success Modal
  const showEditSuccessModal = () => {
    MySwal.fire({
      title: (
        <div className="flex flex-col items-center py-2">
          <div className="w-16 h-16 mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <span className="text-xl font-bold text-gray-900">Success!</span>
        </div>
      ),
      html: (
        <div className="text-center">
          <p className="text-gray-700 mb-4">Card updated successfully!</p>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="font-medium text-gray-800 mb-2">Updated Details:</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Title:</span>
                <span className="font-semibold">{title}</span>
              </div>
              {price !== originalData?.price && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Price:</span>
                  <span className="font-semibold text-[#b59410]">£{parseFloat(price).toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ),
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: 'Go to Dashboard',
      cancelButtonText: 'Stay on Page',
      confirmButtonColor: '#10B981',
      cancelButtonColor: '#6B7280',
      customClass: {
        confirmButton: 'px-4 py-2 rounded-lg font-medium',
        cancelButton: 'px-4 py-2 rounded-lg font-medium'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/dla-shop');
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        fetchCard();
      }
    });
  };

  // Show Confirm Modal
  const showConfirmModal = () => {
    return MySwal.fire({
      title: (
        <div className="flex items-center justify-center space-x-2">
          <span className="text-xl font-bold text-gray-900">Update Card?</span>
        </div>
      ),
      html: (
        <div>
          <p className="text-gray-700 mb-3">Are you sure you want to update this card?</p>
          
          {originalData && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="font-medium text-blue-800 mb-2">Changes to be saved:</p>
              <div className="text-sm space-y-1">
                {title !== originalData.title && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Title:</span>
                    <span className="font-semibold">{title}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ),
      showCancelButton: true,
      confirmButtonColor: '#10B981',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, Update',
      cancelButtonText: 'Cancel'
    });
  };

  // Show Loading Modal
  const showLoadingModal = () => {
    MySwal.fire({
      title: (
        <div className="flex items-center justify-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
          <span className="text-lg font-medium text-gray-900">Updating Card...</span>
        </div>
      ),
      html: <p className="text-gray-600 mt-2">Please wait while we update your card.</p>,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false
    });
  };

  // Handle price change
  const handlePriceChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setPrice(value);
    }
  };

  // Update card
  const updateCard = async (formData) => {
    try {
      const token = getToken();
      
      const response = await fetch(`https://backend.dlapakistan.com/api/dla-shop/cards/${id}/update/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update card');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating card:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (id) {
      fetchCard();
    }
  }, [id]);

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showErrorModal('File size should be less than 5MB');
        return;
      }
      setSelectedImage(file);
      setExistingImage(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Remove selected image with confirmation
  const removeImage = () => {
    Swal.fire({
      title: 'Remove Image?',
      text: 'Are you sure you want to remove this image?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, remove it',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        setSelectedImage(null);
        setExistingImage(null);
        setImagePreview(null);
        Swal.fire('Removed!', 'Image has been removed.', 'success');
      }
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      showErrorModal('Please enter card title');
      return;
    }
    
    if (!description.trim() || description === '<p><br></p>') {
      showErrorModal('Please enter card description');
      return;
    }

    const priceValue = parseFloat(price);
    if (isNaN(priceValue) || priceValue < 0) {
      showErrorModal('Please enter a valid price (0 or greater)');
      return;
    }

    // Check if there are any changes
    const hasChanges = originalData && (
      title !== originalData.title ||
      description !== originalData.description ||
      price !== originalData.price ||
      isFreeForMembers !== originalData.is_free_for_members ||
      (selectedImage || (!imagePreview && originalData.image_url))
    );

    if (!hasChanges) {
      Swal.fire({
        title: 'No Changes',
        text: 'You haven\'t made any changes to update.',
        icon: 'info',
        confirmButtonColor: '#3B82F6',
        confirmButtonText: 'OK'
      });
      return;
    }

    const confirmResult = await showConfirmModal();
    if (!confirmResult.isConfirmed) {
      return;
    }

    setUpdating(true);
    showLoadingModal();

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('price', price);
      formData.append('is_free_for_members', isFreeForMembers);
      
      if (selectedImage) {
        formData.append('image', selectedImage);
      } 
      else if (!existingImage && !selectedImage) {
        formData.append('remove_image', 'true');
      }

      await updateCard(formData);
      
      Swal.close();
      showEditSuccessModal();
      
    } catch (error) {
      Swal.close();
      console.error('Error updating card:', error);
      showErrorModal(error.message || 'Failed to update card');
    } finally {
      setUpdating(false);
    }
  };

  // Reset form
  const resetForm = () => {
    if (!originalData) return;

    const hasChanges = (
      title !== originalData.title ||
      description !== originalData.description ||
      price !== originalData.price ||
      isFreeForMembers !== originalData.is_free_for_members ||
      (selectedImage || (!imagePreview && originalData.image_url))
    );

    if (!hasChanges) {
      Swal.fire({
        title: 'No Changes',
        text: 'You haven\'t made any changes yet.',
        icon: 'info',
        confirmButtonColor: '#3B82F6',
        confirmButtonText: 'OK'
      });
      return;
    }

    Swal.fire({
      title: 'Reset Changes?',
      text: 'Are you sure you want to reset all changes?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, reset',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        if (originalData) {
          setTitle(originalData.title);
          setDescription(originalData.description);
          setPrice(originalData.price);
          setIsFreeForMembers(originalData.is_free_for_members);
          
          if (originalData.image_url) {
            setExistingImage(originalData.image_url);
            setImagePreview(originalData.image_url);
          } else {
            setSelectedImage(null);
            setExistingImage(null);
            setImagePreview(null);
          }
        }
        
        Swal.fire('Reset!', 'All changes have been reset.', 'success');
      }
    });
  };

  const isFree = parseFloat(price) === 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50/30 to-green-50/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/30 to-green-50/30">
      <header className="sticky top-0 z-10 bg-white border-b border-emerald-100 px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link 
              to="/dla-shop" 
              className="flex items-center px-4 py-2 hover:bg-emerald-50 rounded-xl transition-colors text-emerald-700"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Link>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={resetForm}
              disabled={updating}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset Changes
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Edit Card</h1>
          </div>
        </div>
      </header>

      <main className="px-8 py-8">
        <div className="max-w-6xl mx-auto">
          
          {error && (
            <div className="mb-8 bg-red-50 border border-red-200 rounded-2xl p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-red-700 text-sm">
                    {error}
                  </div>
                  <button 
                    onClick={fetchCard}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            <div className="lg:col-span-2 space-y-8">
              
              <div className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-sm">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Card</h1>
                <p className="text-gray-600">
                  Update your card information. All changes will be saved.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Card Image</h2>
                    <p className="text-sm text-gray-500 mt-1">Update or change your card image</p>
                  </div>
                  <span className="text-sm font-medium text-emerald-600">* Optional</span>
                </div>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                
                {imagePreview ? (
                  <div className="relative">
                    <div className="border-2 border-emerald-200 rounded-xl overflow-hidden">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-64 object-cover"
                      />
                    </div>
                    <div className="absolute bottom-4 right-4 flex space-x-2">
                      <button
                        type="button"
                        onClick={triggerFileInput}
                        className="px-4 py-2 bg-white text-emerald-700 border border-emerald-300 rounded-lg hover:bg-emerald-50 transition-colors"
                      >
                        Change Image
                      </button>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={triggerFileInput}
                    className="border-2 border-dashed border-emerald-300 rounded-xl p-12 text-center hover:border-emerald-400 transition-colors cursor-pointer bg-gradient-to-br from-emerald-50/30 to-green-50/30"
                  >
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center">
                      <ImageIcon className="w-12 h-12 text-emerald-600" />
                    </div>
                    <p className="text-gray-700 font-medium text-lg mb-2">Click to upload image</p>
                    <p className="text-emerald-500">PNG, JPG, GIF • Max 5MB</p>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-sm">
                <form onSubmit={handleSubmit} className="space-y-8">
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Card Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-lg"
                      placeholder="Enter card title"
                      required
                      disabled={updating}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Bold className="w-4 h-4" />
                        <Italic className="w-4 h-4" />
                        <Underline className="w-4 h-4" />
                        <span>Rich text editor</span>
                      </div>
                    </div>
                    
                    <div className="border border-gray-300 rounded-xl overflow-hidden">
                      <JoditEditor
                        ref={editor}
                        value={description}
                        config={config}
                        onBlur={newContent => setDescription(newContent)}
                        onChange={newContent => {}}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm text-gray-500">
                        Supports rich text formatting
                      </span>
                      <span className="text-sm text-gray-500">
                        {description.replace(/<[^>]*>/g, '').length}/5000 characters
                      </span>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Price Information <span className="text-red-500">*</span>
                      </label>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Tag className="w-4 h-4 inline mr-2" />
                            Price for Non-Members
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500">£</span>
                            </div>
                            <input
                              type="text"
                              value={price}
                              onChange={handlePriceChange}
                              className="w-full pl-8 pr-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                              placeholder="0.00"
                              required
                              disabled={updating}
                            />
                          </div>
                          <p className="mt-2 text-sm text-gray-500">
                            Enter 0.00 for FREE products
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            DLA Members Access
                          </label>
                          <div className="space-y-3">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id="freeForMembers"
                                checked={isFreeForMembers}
                                onChange={(e) => setIsFreeForMembers(e.target.checked)}
                                className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                                disabled={updating}
                              />
                              <label htmlFor="freeForMembers" className="ml-3 text-sm text-gray-700">
                                Free for DLA Members
                              </label>
                            </div>
                            <p className="text-xs text-gray-500">
                              When checked, DLA members get this product for FREE
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4">
                      <h4 className="font-medium text-emerald-800 mb-2">Price Preview</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Non-Members:</span>
                          <span className="font-semibold text-[#b59410]">
                            {isFree ? 'FREE' : `£${parseFloat(price).toFixed(2)}`}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">DLA Members:</span>
                          <span className="font-semibold text-green-600">
                            {isFreeForMembers ? 'FREE' : `£${parseFloat(price).toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 flex space-x-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      disabled={updating}
                      className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-all duration-300 flex items-center justify-center text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Reset Changes
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="flex-1 px-6 py-4 bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium rounded-xl hover:from-emerald-600 hover:to-green-600 transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {updating ? (
                        <>
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                          Updating Card...
                        </>
                      ) : (
                        <>
                          <Save className="w-6 h-6 mr-3" />
                          Update Card
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-sm sticky top-24">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Live Preview</h2>
                
                {imagePreview && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Image Preview</h3>
                    <div className="border-2 border-emerald-100 rounded-xl overflow-hidden">
                      <img 
                        src={imagePreview} 
                        alt="Card preview" 
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Card Preview</h3>
                  <div className="border border-gray-200 rounded-xl p-4 bg-gradient-to-br from-emerald-50/20 to-green-50/20">
                    {title ? (
                      <h4 className="text-lg font-bold text-gray-900 mb-2">{title}</h4>
                    ) : (
                      <h4 className="text-lg font-bold text-gray-400 mb-2">Card Title</h4>
                    )}
                    
                    {description ? (
                      <div 
                        className="text-gray-600 text-sm prose prose-emerald max-w-none"
                        dangerouslySetInnerHTML={{ __html: description.substring(0, 200) + (description.length > 200 ? '...' : '') }}
                      />
                    ) : (
                      <p className="text-gray-400 text-sm">Card description will appear here</p>
                    )}
                    
                    <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Non-Members:</span>
                        <span className="font-semibold text-[#b59410]">
                          {isFree ? 'FREE' : `£${parseFloat(price).toFixed(2)}`}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">DLA Members:</span>
                        <span className="font-semibold text-green-600">
                          {isFreeForMembers ? 'FREE' : `£${parseFloat(price).toFixed(2)}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100">
                  <div className="flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-emerald-600" />
                    </div>
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-3">
                    Card will appear like this in collection
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 border border-emerald-200">
                <h3 className="font-semibold text-emerald-800 mb-3">📝 Editing Instructions</h3>
                <ul className="space-y-2 text-sm text-emerald-700">
                  <li className="flex items-start">
                    <span className="text-emerald-500 mr-2">•</span>
                    <span>Update title if needed</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-500 mr-2">•</span>
                    <span>Edit description with rich text</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-500 mr-2">•</span>
                    <span>Update price for non-members</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-500 mr-2">•</span>
                    <span>Toggle member free access</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-500 mr-2">•</span>
                    <span>Change or remove image</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-500 mr-2">•</span>
                    <span>Click "Update Card" to save</span>
                  </li>
                </ul>
              </div>
            </div>
          </div> 
        </div>
      </main>
    </div>
  );
};

export default EditCardPage;